/**
 * The generation side's inputs and outputs.
 *
 * The design compiler is invoked RARELY and never on an interaction path — it
 * emits a layout spec, the runtime compiles and caches it. AI as design
 * compiler, not runtime.
 */

import type { TLayoutSpec, Role, RoleKind, Density, AppTemplate } from '@morph/spec';

/** What the generator knows about the objects it is laying out. Deliberately
 *  content-free: role kinds and counts, never values. Feeding the compiler real
 *  content would make its output content-dependent and unpoolable. */
export interface ObjectShape {
  cardinality: 'single' | 'small-set' | 'large-set';
  /** Roles present across the objects, with how often each is populated.
   *  A role populated 5% of the time should not become the hero. */
  roles: ReadonlyArray<{ role: Role; kind: RoleKind; coverage: number }>;
  /** Distinct values / total, per enum role. Low cardinality groups well. */
  enumCardinality?: Readonly<Record<string, number>>;
  /** Bounded distinct-value count per ordered scalar role (`number`, `score`,
   *  or `date`). No sample, endpoint, or other user value crosses the shape
   *  boundary — @Codex's ruling on 0079: *"carrying a suggested threshold in
   *  the pooled layout would cross the value/privacy boundary and make the spec
   *  content dependent."* Coverage alone cannot say whether a threshold control
   *  could partition anything: a role can be on every object and hold one value
   *  on every object. */
  orderedCardinality?: Readonly<Record<string, number>>;
  /** Distinct VALUE PAIRS held by two enum roles together, keyed by
   *  `enumPairKey`. The joint distribution `enumCardinality` cannot express:
   *  two roles can each hold four values and still never cross, in which case
   *  grouping by one and sub-grouping by the other draws a single sub-value
   *  under every column and no heading at all.
   *
   *  Counted only over objects carrying BOTH roles, so it under-reports a pool
   *  where one role is patchy — the renderer buckets a missing value under its
   *  own heading and this does not. Under-reporting is the safe direction: it
   *  withholds a second grouping that might have drawn, and never asks for one
   *  that cannot. */
  enumPairCardinality?: Readonly<Record<string, number>>;
  /**
   * Per derivative role, whether EVERY object carrying its source also carries
   * it. Task `0131`.
   *
   * The one fact per-role coverage cannot express, and @Codex found three
   * defects in a row that each inferred it from a number that cannot carry it:
   * *"two coverages of 0.5 are equally consistent with total overlap and with
   * none."* A card that wants to draw the cheap picture instead of the source
   * needs containment, not a comparison.
   *
   * Keyed by the DERIVATIVE role — `DERIVATIVE_OF` in the vocabulary says which
   * source each stands in for. `true` means the derivative's population
   * contains the source's, so binding the derivative alone loses no picture.
   *
   * OPTIONAL, and absent means "not known" rather than "false": a shape
   * assembled by something other than the two derivers must not be read as
   * having proved a containment it never looked for. Both derivers fill it in,
   * because both already walk every object.
   */
  derivativeCovers?: Readonly<Record<string, boolean>>;
  hasMedia: boolean;
  relationDensity: number;
  /**
   * Share of objects holding a reference into ANOTHER document.
   *
   * Different in kind from `relationDensity`, which is why it is a second
   * number rather than a bigger first one. A reference to a peer in the same
   * collection points at something already on screen, so surfacing it on every
   * card is repetition — that is what `relationSurfaceThreshold` is set high
   * to prevent. A reference into a document this lens does not show cannot be
   * on screen at all: draw it or the reference is invisible.
   *
   * OPTIONAL, and the one field on this interface that a bag of objects cannot
   * supply. Only a `Scope` knows which documents it declared, so
   * `store/src/shape.ts` fills it in and `generation/src/shape.ts` leaves it
   * absent rather than guessing at zero — the same split, and the same reason,
   * as the denominator note in those two files.
   */
  crossDocDensity?: number;
  /**
   * This shape was measured over a SAMPLE rather than the whole scope.
   *
   * OPTIONAL and absent by default, for the same reason `crossDocDensity` is:
   * only a `Scope` knows its own denominator, so `store/src/shape.ts` fills it
   * in and a bag of objects leaves it absent.
   *
   * It exists here because `deriveTemplate` cannot be safe without it. Coverage
   * over a sample proves unanimity over the SAMPLE, and a 501-object pool whose
   * first 500 agree would seed every new object with a default the 501st
   * refutes. `StoredShape extends ObjectShape`, so a sampled shape is already
   * assignable to every consumer of this interface — declining unanimity at one
   * call site leaves the exported contract wrong for the next caller, and the
   * contract is what ships.
   */
  sampled?: boolean;
  /**
   * The pool DECLARES a relation-mutable reference, whether or not any object
   * currently carries one.
   *
   * A different fact from `relationDensity`, and the difference is a product
   * defect if they are conflated. Density is a tuning input: how many objects
   * hold a reference decides whether a relation earns a region of its own.
   * Declaration is a REACHABILITY fact: decision 0016 puts attach and detach on
   * a `node-slot` and nowhere else, so a lens that emits no slot offers no
   * gesture — and a pool below the surfacing threshold then has **no way to
   * create the reference that would raise its density**.
   *
   * @Codex named the trap: *"including a new pool with zero relations … the
   * pool has no gesture capable of creating the relation that would raise its
   * density; the implementation therefore supports an object's first reference
   * only when other objects have already bootstrapped the pool."* Films works
   * only because most films already have a director. A pool nobody has
   * bootstrapped would be permanently unwritable.
   *
   * Safe to surface on declaration alone, because the RENDERER already decides
   * the read-only case: a node slot with no targets and no `link` grant draws
   * nothing at all. So a reader sees an empty region only where they could have
   * filled it.
   *
   * Absent from a shape derived out of a bag of objects — `deriveShape` cannot
   * know what a pool declares, only what its objects happen to hold — which is
   * also why the tuning probes leave it unset and the thresholds stay live.
   */
  declaresRelations?: boolean;
}

export type UseContext = 'reading' | 'authoring' | 'browsing' | 'comparing' | 'studying';

export const TEMPLATE_VIEWS = ['board', 'table', 'calendar', 'timeline'] as const;
export type TemplateView = (typeof TEMPLATE_VIEWS)[number];

export interface GenerationRequest {
  shape: ObjectShape;
  context: UseContext;
  /** Priors from the taste model. Absent on cold start. */
  taste?: TastePriors;
  /** Genomes retrieved from the pool, scored against this request. Candidates,
   *  never answers. */
  poolPriors?: readonly RetrievedGenome[];
  /** The currently accepted spec, if any. Generation is a REVISION of what the
   *  user already has, not a fresh design — this is what keeps churn low. */
  accepted?: TLayoutSpec;
  /**
   * Roles the caller's schema DECLARES beyond what the shape carries —
   * @Codex, P2 on `2ffce98`. The shape counts populated values, so clearing
   * a role's last value makes it vanish from `shape.roles` while its
   * manifest key and its editable empty field remain real; the revision
   * compatibility check then read that vanishing as schema removal and
   * refused every later accepted revision — density, palette, a pool
   * suggestion — until some value was restored by another route. A
   * declared-but-empty role is available, not gone, and only the caller
   * can say so: the shape cannot see a manifest.
   */
  declaredRoles?: readonly Role[];
  /**
   * How many values each DECLARED enum may take — task `0202`, and it is the
   * KIND half of what `declaredRoles` restores.
   *
   * `declaredRoles` brings a cleared role's PRESENCE back, so the field is
   * drawn and the person has somewhere to put a value. It says nothing about
   * what the role IS, and `chooseableEnums` needs a count: a cleared enum is
   * absent from `shape.roles` and from `shape.enumCardinality` alike, so it
   * came back read-only with an empty dropdown — a surface that says the
   * property exists and offers no way to give it a value. The repair restored
   * the presence and lost the kind, which is the one-dimension-short this
   * family keeps arriving at.
   *
   * Only the caller can supply it, for the same reason `declaredRoles` exists:
   * the shape counts what is populated and cannot see a manifest. A pool that
   * declares its options knows the number without an object carrying one.
   *
   * IT IS THE UNION, AND THAT IS WHY IT IS NAMED FOR THE CONTROL — task
   * `0215`. This was `declaredEnumCardinality`, a count of the DECLARED
   * options, and the generator took the larger of it and the shape's observed
   * count. Two counts cannot be unioned: five declared and five observed that
   * barely overlap read as five while the host filled the dropdown with ten,
   * so an enum could pass a threshold of eight and open a control of ten.
   *
   * The host holds both sets and counts them, so what arrives here is the
   * number of options the control will actually hold. A caller that cannot
   * compute the union may still send the declared count — it is the old lower
   * bound, worse but not wrong — and the generator still floors it with the
   * observed count for exactly that case.
   */
  enumOptionCount?: Readonly<Record<string, number>>;
  /**
   * A NAMED APP LOOK, asked for by name — decision 0055. A hard constraint,
   * not a taste: it enters the request beside shape and taste and expands to
   * the required region purposes, component families and visual tokens of
   * that app, and the generator emits the whole presentation or refuses in
   * words. Taste may still vary what the template declares variable.
   */
  template?: AppTemplate;
  /** Which of a named look's VIEWS to emit — Notion's Board, Table,
   *  Calendar, Timeline; the look's default when absent. A view is the
   *  same page with a different subject presentation, so switching one
   *  is a regeneration the person asked for by name. */
  templateView?: TemplateView;
  /**
   * The runtime MODULES the host has registered, by id. A template that
   * needs a module the host cannot draw refuses up front rather than
   * emitting a spec whose chrome would arrive as a "not yet implemented"
   * note — 0055 control 1. Absent means the caller is not saying, and the
   * generator trusts the vocabulary's own registry.
   */
  capabilities?: readonly string[];
}

export interface TastePriors {
  density?: Density;
  typography?: TLayoutSpec['typography'];
  palette?: TLayoutSpec['palette'];
  /** Roles the user has demoted or hidden. Honoured absolutely — an explicit
   *  hide that keeps coming back is the fastest way to lose a user. */
  suppressedRoles?: readonly Role[];
  /** Roles the person has asked to lead ordinary metadata. Media and identity
   *  still lead because they make the object recognisable. */
  prominentRoles?: readonly Role[];
  // `pinned` and `novelty` stood here until decision 0045 withdrew them.
  //
  // Both declared a behaviour and were read by nothing. 0045 applied 0036's
  // compilation-target test to each and found neither had one: `pinned` was a
  // `string[]` naming "elements" without saying whether those are node ids,
  // roles, paths, module slots or candidate ids — *"those choices have
  // different portability and privacy consequences"* — and `novelty` put the
  // decision of WHETHER to propose inside a value consumed after a request has
  // already been made, *"confusing two boundaries"*.
  //
  // They are not commented out and they are not deprecated: 0045's control 5 is
  // that no exported production type contains them, *"even if no caller sets
  // it"*. The re-entry conditions are in the decision, not here, so that
  // rebuilding one means reading what it has to prove rather than uncommenting
  // a field. Their sibling `accepted` survived the same test — it has a
  // compilation target, the structure being revised.
}

/**
 * One pooled genome, plus how well it fits THIS request.
 *
 * Called `PoolPrior` until decision 0040, which found the name doing two jobs:
 * this — a scored retrieval candidate — and `FoldOptions.poolPrior`, the
 * `knob -> value -> probability` base the taste model consumes. They are not
 * producer and consumer, so code satisfying both under one name built two
 * disconnected pool paths. `0040`: *"phase 2 must rename it rather than
 * preserve the ambiguity."*
 */
export interface RetrievedGenome {
  genomeId: string;
  /** 0..1 fit against this request's shape and context. */
  score: number;
  structure: unknown;
  density: Density;
  typography: TLayoutSpec['typography'];
  palette: TLayoutSpec['palette'];
}

export type GenerationOutcome =
  | { ok: true; spec: TLayoutSpec; provenance: Provenance }
  | { ok: false; reason: string; problems: readonly string[] };

export interface Provenance {
  /** Which generator produced it — the baseline is deterministic and always
   *  available; a model-backed one plugs in behind the same interface. */
  generator: string;
  /** Pool genomes that informed it, for attribution and for measuring whether
   *  the pool is actually helping. */
  priors: readonly string[];
  attempts: number;
}

/** A generator is anything that proposes a spec. Provider-neutral by
 *  construction: the pipeline validates and repairs whatever comes back, so a
 *  model adapter and the deterministic baseline are interchangeable. */
export interface Generator {
  readonly name: string;
  propose(req: GenerationRequest): Promise<TLayoutSpec> | TLayoutSpec;
}
